import Login from "./pages/login";
import { Navigate, Route, Routes } from "react-router-dom";
import Dashboard from "./pages/Dashboard";
import { IRoute } from "./common/types";
import {
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  SafetyOutlined,
  CreditCardOutlined,
  BellOutlined,
  TransactionOutlined,
  DollarOutlined,
  BankOutlined,
  CarOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { RobinLayout } from "./components/RobinLayout";
import Profile from "./pages/Profile";
import ProfileUpdate from "./pages/ProfileUpdate";
import PartnerList from "./pages/PartnerList";
import DriverList from "./pages/DriverList";
import DriverDetail from "./pages/DriverDetail";
import BeneficiariesList from "./pages/BeneficiariesList";
import WithdrawalsList from "./pages/WithdrawalsList";
import TransactionsList from "./pages/TransactionsList";
import PartnersBalance from "./pages/PartnersBalance";
import PartnerCategoryList from "./pages/PartnerCategoryList";
import PartnerMutate from "./pages/PartnerMutate";
import PartnerTracking from "./pages/PartnerTracking";
import PartnerPromoCodesPage from "./pages/PartnerPromoCodesPage";
import PlanList from "./pages/PlanList";
import PlanMutate from "./pages/PlanMutate";
import PromoCodeList from "./pages/PromoCodeList";
import PromoCodeMutate from "./pages/PromoCodeMutate";
import PermissionList from "./pages/PermissionList";
import { usePermission } from "./hooks/usePermission";
import PermissionMutate from "./pages/PermissionMutate";
import NotificationSend from "./pages/NotificationSend";
import FinanceTree from "./pages/FinanceTree";
import FinanceTransactionList from "./pages/FinanceTransactionList";
import FinanceTransactionCreate from "./pages/FinanceTransactionCreate";
import FinanceTransactionEdit from "./pages/FinanceTransactionEdit";
import FinanceTransactionView from "./pages/FinanceTransactionView";
import FinanceTreeTransactionLines from "./pages/FinanceTreeTransactionLines";
import Maintenance from "./pages/Maintenance";
import RushHourConfig from "./pages/RushHourConfig";
import UnassignedOrderDetails from "./pages/UnassignedOrderDetails";
import UnassignedOrdersList from "./pages/UnassignedOrdersList";

export function AppRoute() {
  const AppRoutes: IRoute[] = [
    {
      path: "/dashboard",
      index: true,
      showInMenu: true,
      icon: <DashboardOutlined />,
      label: "label.menu.dashboard",
      component: <Dashboard />,
    },
    {
      path: "/partners",
      showInMenu: true,
      asParent: true,
      icon: <TeamOutlined />,
      label: "label.menu.partners",
      component: <Navigate to="/partners/list" replace />,
    },
    {
      path: "/partners/list",
      showInMenu: true,
      asChild: true,
      icon: <TeamOutlined />,
      label: "label.menu.partner_list",
      component: <PartnerList />,
    },
    {
      path: "/partners/categories",
      showInMenu: true,
      asChild: true,
      icon: <TeamOutlined />,
      label: "label.menu.partner_category_list",
      component: <PartnerCategoryList />,
    },
    {
      path: "/partners/tracking",
      showInMenu: false,
      asChild: true,
      icon: <TeamOutlined />,
      label: "label.menu.partner_tracking",
      component: <PartnerTracking />,
    },
    {
      path: "/partners/promo-codes",
      showInMenu: true,
      asChild: true,
      icon: <TagOutlined />,
      label: "label.menu.partner_promo_codes",
      component: <PartnerPromoCodesPage />,
    },
    {
      path: "/drivers",
      showInMenu: true,
      asParent: true,
      icon: <CarOutlined />,
      label: "label.menu.drivers",
      component: <></>,
    },
    {
      path: "/drivers/list",
      showInMenu: true,
      asChild: true,
      icon: <CarOutlined />,
      label: "label.menu.drivers",
      component: <DriverList />,
    },
    {
      path: "/drivers/beneficiaries",
      showInMenu: true,
      asChild: true,
      icon: <BankOutlined />,
      label: "label.menu.driver_beneficiaries",
      component: <BeneficiariesList />,
    },
    {
      path: "/drivers/withdraw-requests",
      showInMenu: true,
      asChild: true,
      icon: <DollarOutlined />,
      label: "label.menu.withdraw_requests",
      component: <WithdrawalsList />,
    },
    {
      path: "/drivers/:id",
      icon: <CarOutlined />,
      component: <DriverDetail />,
    },
    {
      path: "/transactions",
      showInMenu: true,
      asParent: true,
      icon: <TransactionOutlined />,
      label: "label.menu.transactions",
      component: <></>,
    },
    {
      path: "/transactions/list",
      showInMenu: true,
      asChild: true,
      icon: <CreditCardOutlined />,
      label: "label.menu.transactions_list",
      component: <TransactionsList />,
    },
    {
      path: "/transactions/partners-balance",
      showInMenu: true,
      asChild: true,
      icon: <DollarOutlined />,
      label: "label.menu.partners_balance",
      component: <PartnersBalance />,
    },
    {
      path: "/finance",
      showInMenu: true,
      asParent: true,
      icon: <BankOutlined />,
      label: "label.menu.finance",
      component: <></>,
    },
    {
      path: "/finance/tree",
      showInMenu: true,
      asChild: true,
      icon: <BankOutlined />,
      label: "label.menu.finance_tree",
      component: <FinanceTree />,
    },
    {
      path: "/finance/transactions",
      showInMenu: true,
      asChild: true,
      icon: <TransactionOutlined />,
      label: "label.menu.finance_transactions_list",
      component: <FinanceTransactionList />,
    },
    {
      path: "/finance/transactions/create",
      icon: <TransactionOutlined />,
      component: <FinanceTransactionCreate />,
    },
    {
      path: "/finance/transactions/edit/:id",
      icon: <TransactionOutlined />,
      component: <FinanceTransactionEdit />,
    },
    {
      path: "/finance/transactions/view/:id",
      icon: <TransactionOutlined />,
      component: <FinanceTransactionView />,
    },
    {
      path: "/finance/tree-transaction-lines",
      icon: <BankOutlined />,
      component: <FinanceTreeTransactionLines />,
    },
    {
      path: "/orders/unassigned",
      showInMenu: true,
      icon: <ShoppingCartOutlined />,
      label: "label.menu.unassigned_orders",
      component: <UnassignedOrdersList />,
    },
    {
      path: "/orders/unassigned/:id",
      icon: <ShoppingCartOutlined />,
      component: <UnassignedOrderDetails />,
    },
    {
      path: "/settings",
      showInMenu: true,
      asParent: true,
      icon: <SettingOutlined />,
      label: "label.menu.settings",
      component: <Navigate to="/settings/notifications" replace />,
    },
    {
      path: "/settings/notifications",
      showInMenu: true,
      asChild: true,
      icon: <BellOutlined />,
      label: "label.menu.send_notification",
      component: <NotificationSend />,
    },
    {
      path: "/settings/maintenance",
      showInMenu: true,
      asChild: true,
      icon: <ToolOutlined />,
      label: "label.menu.maintenance",
      component: <Maintenance />,
    },
    {
      path: "/settings/rush-hour-config",
      showInMenu: true,
      asChild: true,
      icon: <ClockCircleOutlined />,
      label: "label.menu.rush_hour_config",
      component: <RushHourConfig />,
    },
    {
      path: "/partner-category",
      showInMenu: false,
      component: <Navigate to="/partners/categories" replace />,
    },
    {
      path: "/partners/mutate",
      icon: <DashboardOutlined />,
      component: <PartnerMutate />,
    },
    {
      path: "/partners/mutate/:id",
      icon: <DashboardOutlined />,
      component: <PartnerMutate />,
    },
    {
      path: "/partners/view/:id",
      icon: <DashboardOutlined />,
      component: <PartnerList />, // For now, using PartnerList as view component
    },

    {
      path: "/profile",
      component: <Profile />,
    },
    {
      path: "/profile/update",
      component: <ProfileUpdate />,
    },
    {
      path: "/login",
      ignoreLayout: true,
      component: <Login />,
    },
  ];
  const perms = usePermission([
    "getAllPermissions",
    "getAllPlans",
  ]);
  if (perms["getAllPermissions"])
    AppRoutes.push(
      {
        path: "/settings/permission",
        showInMenu: true,
        asChild: true,
        icon: <SafetyOutlined />,
        label: "label.menu.permission_list",
        component: <PermissionList />,
      },
      {
        path: "/settings/permission/mutate",
        component: <PermissionMutate />,
      },
      {
        path: "/settings/permission/mutate/:id",
        component: <PermissionMutate />,
      },
      {
        path: "/permission",
        showInMenu: false,
        component: <Navigate to="/settings/permission" replace />,
      },
      {
        path: "/permission/mutate",
        component: <Navigate to="/settings/permission/mutate" replace />,
      },
      {
        path: "/permission/mutate/:id",
        component: <PermissionMutate />,
      }
    );
  if (perms["getAllPlans"]) {
    AppRoutes.push(
      {
        path: "/settings/plans",
        showInMenu: true,
        asChild: true,
        icon: <CreditCardOutlined />,
        label: "label.menu.plans",
        component: <PlanList />,
      },
      {
        path: "/settings/plans/mutate",
        component: <PlanMutate />,
      },
      {
        path: "/settings/plans/mutate/:id",
        component: <PlanMutate />,
      },
      {
        path: "/plans",
        showInMenu: false,
        component: <Navigate to="/settings/plans" replace />,
      },
      {
        path: "/plans/mutate",
        component: <Navigate to="/settings/plans/mutate" replace />,
      },
      {
        path: "/plans/mutate/:id",
        component: <PlanMutate />,
      }
    );
  }
  AppRoutes.push({
    path: "/notifications/send",
    showInMenu: false,
    component: <Navigate to="/settings/notifications" replace />,
  });
  AppRoutes.push(
    {
      path: "/settings/promo-codes",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    },
    {
      path: "/settings/promo-codes/mutate",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    },
    {
      path: "/settings/promo-codes/mutate/:id",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    },
    {
      path: "/promo-codes",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    },
    {
      path: "/promo-codes/mutate",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    },
    {
      path: "/promo-codes/mutate/:id",
      showInMenu: false,
      component: <Navigate to="/partners/promo-codes" replace />,
    }
  );
  return (
    <Routes>
      {AppRoutes.map((route) => {
        if (route.ignoreLayout)
          return (
            <Route
              key={route.path}
              path={route.path}
              element={route.component}
            />
          );
        else if (route.index)
          return [
            <Route
              key="/"
              path="/"
              index
              element={
                <RobinLayout AppRoutes={AppRoutes} children={route.component} />
              }
            />,
            <Route
              key={route.path}
              path={route.path}
              element={
                <RobinLayout AppRoutes={AppRoutes} children={route.component} />
              }
            />,
          ];
        else
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <RobinLayout AppRoutes={AppRoutes} children={route.component} />
              }
            />
          );
      })}
    </Routes>
  );
}
